import React, { forwardRef, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { margin, MarginProps } from 'styled-system'
import { default as Calendar } from 'react-datepicker'
import { startOfDay } from 'date-fns'
import 'react-datepicker/dist/react-datepicker.css'

import { DateFormat } from 'types/global'
import { formatDatetime, parseDate } from 'utils/datetime'

import TextInput from '../../TextInput'
import { TextInputProps } from '../../TextInput'
import Spinner from '../../Spinner'
import * as Popper from 'popper.js'
import { BodyPortal, Box } from 'components'
import InlineEditBtn from 'modules/Leads/LeadDetailV2/shared/InlineEditBtn'
import useOnClickOutside from 'hooks/use-click-outside-hooks'
import InlineInputButtons from 'components/Buttons/InlineInputButtons/v2'

export type PickerSize = 'small' | 'regular'
export type PopupPosition = 'top-start' | 'top-end' | 'bottom-end' | 'bottom-start' | 'bottom'

type OnChangeRangeResult = {
  selectsRange: true
  onChange: (value: [Date, Date]) => void
}
type OnChangeSingleResult = {
  selectsRange?: false
  onChange: (value: Date) => void
}

type OnChangeResult = OnChangeRangeResult | OnChangeSingleResult

export type DatePickerProps = Omit<TextInputProps, 'onChange' | 'value'> & {
  customInput?: JSX.Element
  closeOnSelect?: boolean
  dateFormat?: DateFormat
  defaultToday?: boolean
  disablePast?: boolean
  disableNext?: boolean
  endDate?: Date
  inline?: boolean
  maxDate?: Date | null
  minDate?: Date | null
  onFocusCustomInput?: (e: React.FocusEvent<HTMLInputElement>) => void
  selectsStart?: boolean
  selectsEnd?: boolean
  showIcon?: boolean
  startDate?: Date
  pickerSize?: PickerSize
  textInputInline?: boolean
  value?: Date | string | [Date, Date] | null
  popupPosition?: PopupPosition
  showMonthYearDropdown?: boolean
  popperModifiers?: Popper.Modifiers
  isPortaled?: boolean
  displayNode?: React.ReactNode
} & OnChangeResult

type CustomInputTypeProps = {
  value?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const DatePicker = ({
  addonBefore,
  addonAfter,
  customInput,
  closeOnSelect = true,
  dateFormat = 'dd/MM/yyyy',
  defaultToday = true,
  disabled,
  disablePast = false,
  disableNext,
  endDate,
  error,
  label,
  inline = false,
  maxDate,
  minDate,
  marginRight,
  marginY,
  mb,
  mr,
  name,
  onBlur,
  onChange,
  onFocus,
  onFocusCustomInput,
  placeholder,
  pickerSize = 'regular',
  readOnly,
  selectsEnd,
  selectsStart,
  selectsRange = false,
  showError,
  showIcon = true,
  startDate,
  textInputInline,
  required,
  value,
  variant,
  width,
  height,
  popupPosition,
  popperModifiers,
  isLoading = false,
  showMonthYearDropdown = false,
  isPortaled = false,
  displayNode,
}: DatePickerProps) => {
  const ref = useRef<HTMLInputElement>(null)
  const [isEditBtnDisplay, setEditBtnDisplay] = useState(false)
  const [isEditClicked, setEditClicked] = useState(false)
  const [tempValue, setTempValue] = useState(value)

  useOnClickOutside(ref, () => {
    if (!isEditClicked) {
      return
    }
    applyChanges()
    setEditBtnDisplay(false)
    setEditClicked(false)
  })

  const getCustomInputValue = () => {
    if (selectsRange) {
      if (endDate && startDate) {
        return `${formatDatetime(startDate, dateFormat)} - ${formatDatetime(endDate, dateFormat)}`
      }

      return undefined
    }

    if (Array.isArray(tempValue)) {
      return `${formatDatetime(tempValue[0], dateFormat)} - ${formatDatetime(tempValue[1], dateFormat)}`
    }

    return formatDatetime(tempValue, dateFormat) || undefined
  }

  const CustomInput = forwardRef(({ onBlur, onFocus, onChange }: CustomInputTypeProps, _ref) => (
    <TextInput
      addonBefore={addonBefore}
      addonAfter={addonAfter}
      disabled={disabled}
      error={error}
      icon={showIcon ? ['far', 'calendar-alt'] : undefined}
      inline={textInputInline}
      label={label}
      name={name}
      onBlur={onBlur}
      onFocus={onFocus}
      onChange={onChange}
      placeholder={placeholder ? placeholder : dateFormat?.toUpperCase()}
      showError={showError}
      readOnly={readOnly}
      required={required}
      width={width}
      height={height}
      value={getCustomInputValue()}
      variant={variant}
      autoComplete="off"
    />
  ))

  CustomInput.displayName = 'CustomInput'
  const marginProps = { marginRight, marginY, mb, mr }

  const defaultValue = defaultToday ? new Date() : undefined
  const selected = tempValue
    ? Array.isArray(tempValue)
      ? tempValue[0]
      : typeof tempValue === 'string'
      ? parseDate(tempValue)
      : tempValue
    : defaultValue

  const applyChanges = () => {
    if (Array.isArray(tempValue) && selectsRange === true) {
      onChange(tempValue as [Date, Date] & Date)
      return
    }

    onChange(startOfDay(tempValue as Date) as [Date, Date] & Date)
  }

  const Container = ({ children }: { children: React.ReactNode }) => (
    <DatePickerWrapper
      {...marginProps}
      inline={inline}
      isRangeSelected={Boolean(startDate || endDate)}
      pickerSize={pickerSize}
      textInputInline={textInputInline}
      showMonthYearDropdown={showMonthYearDropdown}
    >
      {children}
    </DatePickerWrapper>
  )

  if (displayNode && !isEditClicked) {
    return (
      <FieldBoxWrapper
        position="relative"
        onClick={() => setEditClicked(true)}
        onMouseEnter={() => setEditBtnDisplay(true)}
        onMouseLeave={() => setEditBtnDisplay(false)}
      >
        {displayNode}
        {isEditBtnDisplay && !disabled && (
          <Box transform="translateY(-50%)" position="absolute" right={0} top="50%" backgroundColor="white">
            <InlineEditBtn />
          </Box>
        )}
      </FieldBoxWrapper>
    )
  }

  return (
    <div onMouseDown={(e) => e.stopPropagation()}>
      <CalendarWrapper ref={ref} disabled={disabled}>
        <Calendar
          calendarContainer={Container}
          popperContainer={isPortaled ? BodyPortal : undefined}
          customInput={customInput || <CustomInput onBlur={onBlur} onFocus={onFocusCustomInput} />}
          dateFormat={dateFormat}
          endDate={endDate}
          inline={inline}
          minDate={minDate || (disablePast ? new Date() : null)}
          maxDate={maxDate || (disableNext ? new Date() : null)}
          name={name}
          readOnly={readOnly}
          onBlur={onBlur}
          popperModifiers={{
            ...popperModifiers,
            offset: {
              offset: '0, -10',
            },
          }}
          onChange={(v) => {
            if (!v) {
              return
            }

            if (Array.isArray(v) && selectsRange === true) {
              setTempValue(v as [Date, Date] & Date)
              return
            }

            setTempValue(startOfDay(v as Date) as [Date, Date] & Date)
          }}
          onFocus={onFocus}
          selected={selected}
          selectsEnd={selectsEnd}
          selectsStart={selectsStart}
          selectsRange={selectsRange}
          shouldCloseOnSelect={closeOnSelect}
          showPopperArrow={false}
          startDate={startDate}
          strictParsing
          useWeekdaysShort={true}
          popperPlacement={popupPosition}
          showMonthDropdown={showMonthYearDropdown}
          showYearDropdown={showMonthYearDropdown}
        />
        <Box>
          <InlineInputButtons
            position="relative"
            onClickCancel={() => {
              setEditBtnDisplay(false)
              setEditClicked(false)
            }}
            onClickConfirm={() => {
              applyChanges()
              setEditBtnDisplay(false)
              setEditClicked(false)
            }}
          />
        </Box>
        {isLoading && (
          <LoadingOverlay>
            <Spinner />
          </LoadingOverlay>
        )}
      </CalendarWrapper>
    </div>
  )
}

const CalendarWrapper = styled.div<{ disabled?: boolean }>`
  display: flex;
  gap: 10px;
  padding: 6px 16px;
  align-items: flex-end;

  .react-datepicker__input-container {
    width: inherit;
  }

  .react-datepicker-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  }
`

const DatePickerWrapper = styled.div<
  MarginProps & {
    inline?: boolean
    isRangeSelected?: boolean
    pickerSize?: PickerSize
    textInputInline?: boolean
    showMonthYearDropdown: boolean
  }
>`
  ${margin}
  background-color: ${(p) => p.theme.colors.white};
  border-radius: 4px;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  display: ${(p) => (p.textInputInline ? 'inline-block' : 'block')};
  position: relative;

  & .react-datepicker {
    border: ${(p) => (p.pickerSize === 'small' ? 20 : 0)}px;
    box-shadow: ${(p) =>
      p.inline
        ? 'none'
        : '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 0 rgba(0, 0, 0, 0.05)'};
    height: ${(p) => (p.pickerSize === 'small' ? 285 : 350)}px;
    width: ${(p) => (p.pickerSize === 'small' ? 278 : 375)}px;
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: 2rem;
  }

  & .react-datepicker__day {
    ${(p) =>
      p.theme.font({
        family: 'roboto',
        color: 'gray7',
        size: p.pickerSize === 'small' ? 14 : 16,
        weight: 'normal',
      })};
    margin: 2px 5px;
    padding: ${(p) => (p.pickerSize === 'small' ? 1 : 5)}px;
  }

  & .react-datepicker__current-month {
    ${({ showMonthYearDropdown }) =>
      showMonthYearDropdown
        ? `display: none;`
        : `
            height: 28px;
            display: flex;
            justify-content: center;
            align-items: center;
        `};
  }

  & .react-datepicker__day-name {
    ${(p) =>
      p.theme.font({
        family: 'roboto',
        color: p.pickerSize === 'small' ? 'gray5' : 'gray7',
        size: p.pickerSize === 'small' ? 10 : 12,
        weight: p.pickerSize === 'small' ? 'bold' : 'normal',
      })};
    margin: ${(p) => (p.pickerSize === 'small' ? '0 5px 0' : '10px 5px 0')};
    padding: ${(p) => (p.pickerSize === 'small' ? '10px 2px 0 0' : '10px 5px 0 5px')};
    text-transform: uppercase;
  }

  & .react-datepicker__day--outside-month {
    visibility: hidden;
  }

  & .react-datepicker__day--in-range,
  & .react-datepicker__day--selected,
  & .react-datepicker__day--keyboard-selected {
    color: ${(p) => p.theme.colors.white} !important;
  }

  ${(p) =>
    p.isRangeSelected
      ? css`
          & .react-datepicker__day--range-start,
          & .react-datepicker__day--range-end,
          & .react-datepicker__day--in-range {
            background-color: ${p.theme.colors.blue1} !important;
          }

          &
            .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range, .react-datepicker__day--range-start, .react-datepicker__day--range-end),
          &
            .react-datepicker__day--selected:not(.react-datepicker__day--in-range, .react-datepicker__day--range-start, .react-datepicker__day--range-end),
          &
            .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--in-range, .react-datepicker__day--range-start, .react-datepicker__day--range-end) {
            background-color: ${(p) => p.theme.colors.white} !important;
            color: ${(p) => p.theme.colors.gray7} !important;
          }
        `
      : css`
          & .react-datepicker__day--selected,
          & .react-datepicker__day--keyboard-selected {
            background-color: ${p.theme.colors.blue1};
          }
        `}

  & .react-datepicker__day--disabled {
    ${(p) =>
      p.theme.font({
        family: 'roboto',
        color: 'gray4',
        size: p.pickerSize === 'small' ? 14 : 16,
        weight: 'normal',
      })};
  }

  & .react-datepicker__header {
    background-color: transparent;
    border-bottom: none;
    padding-top: 1rem;
  }

  & .react-datepicker__month {
    margin: 0;
  }

  & .react-datepicker__month-container {
    float: none;
    height: 100%;
  }

  & .react-datepicker__navigation--next {
    background-image: url('/assets/angle-right.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 7px;
    border: 1px solid;
    border-color: ${(p) => p.theme.colors.gray4};
    border-radius: 4px;
    margin-right: ${(p) => (p.pickerSize === 'small' ? 0 : 15)}px;
    padding: 13px 13px;
    top: 1rem;
  }

  & .react-datepicker__navigation--previous {
    background-image: url('/assets/angle-left.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 7px;
    border: 1px solid;
    border-color: ${(p) => p.theme.colors.gray4};
    border-radius: 4px;
    margin-left: ${(p) => (p.pickerSize === 'small' ? 0 : 15)}px;
    padding: 13px 13px;
    top: 1rem;
  }

  & .react-datepicker__navigation--next:hover,
  & .react-datepicker__navigation--previous:hover {
    background-color: ${(p) => p.theme.colors.gray1};
    border-color: ${(p) => p.theme.colors.blue1};
  }

  & .react-datepicker__input-container {
    width: inherit;
  }

  & .react-datepicker-wrapper {
    width: 100%;
  }
  ${(p) => getCustomCalendarDropdownStyle({ pickerSize: p.pickerSize })}
`

const FieldBoxWrapper = styled(Box)`
  *:hover {
    background: ${({ theme }) => theme.colors.gray1};
  }
`
export const getCustomCalendarDropdownStyle = ({ pickerSize }: { pickerSize?: PickerSize }) => css`
  .react-datepicker__year-read-view,
  .react-datepicker__month-read-view,
  .react-datepicker__month-year-read-view {
    user-select: none;
    border: 1px solid ${(p) => p.theme.colors.gray4};
    height: 28px;
    padding-top: 4px;

    :hover {
      border-color: ${(p) => p.theme.colors.blue1};
    }
  }

  .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__month-read-view--down-arrow,
  .react-datepicker__month-year-read-view--down-arrow {
    top: 5px;
    right: 5px;
  }

  .react-datepicker__month-read-view--selected-month,
  .react-datepicker__year-read-view--selected-year {
    padding-right: 10px;
    ${(p) =>
      p.theme.font({
        family: 'roboto',
        color: 'gray7',
        size: pickerSize === 'small' ? 14 : 16,
        weight: 'bold',
      })};
  }

  .react-datepicker__year-dropdown-container--select,
  .react-datepicker__month-dropdown-container--select,
  .react-datepicker__month-year-dropdown-container--select,
  .react-datepicker__year-dropdown-container--scroll,
  .react-datepicker__month-dropdown-container--scroll,
  .react-datepicker__month-year-dropdown-container--scroll {
    position: relative;
    margin: 0 ${pickerSize === 'small' ? 2 : 5}px;
  }

  .react-datepicker__year-dropdown,
  .react-datepicker__month-dropdown,
  .react-datepicker__month-year-dropdown {
    background-color: ${(p) => p.theme.colors.white};
    top: 0;
    width: fit-content;
    min-width: 100%;
    left: 50%;
    transform: translateX(-50%);
  }

  .react-datepicker__year-option,
  .react-datepicker__month-option,
  .react-datepicker__month-year-option {
    user-select: none;
    position: relative;
    ${(p) =>
      p.theme.font({
        family: 'roboto',
        color: 'gray7',
        size: pickerSize === 'small' ? 14 : 16,
      })}
    line-height: 1.2;
    padding: 0.2rem 0.5rem;
    border-top: 1px solid transparent;
    border-bottom: 1px solid transparent;

    :hover {
      color: ${(p) => p.theme.colors.defaultText};
      background-color: ${(p) => p.theme.colors.gray2};
      border-color: ${(p) => p.theme.colors.gray3};
    }
  }

  .react-datepicker__month-option--selected_month,
  .react-datepicker__year-option--selected_year,
  .react-datepicker__month-option--selected_month:hover,
  .react-datepicker__year-option--selected_year:hover {
    color: ${(p) => p.theme.colors.white};
    background-color: ${(p) => p.theme.colors.blue1};
    border-top: 1px solid ${(p) => p.theme.colors.defaultBorder};
    border-bottom: 1px solid ${(p) => p.theme.colors.defaultBorder};
  }

  .react-datepicker__year-option--selected,
  .react-datepicker__month-option--selected,
  .react-datepicker__month-year-option--selected {
    display: none;
  }
`

const LoadingOverlay = styled.div`
  user-select: none;
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 3rem;
`

export default DatePicker
